import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowUpRight } from "lucide-react";

const months = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

// Accepts new API shape (start_at, cover_image, ...) with graceful fallbacks
// to the legacy mock shape (date, image, ...) so any existing consumer keeps working.
const norm = (event) => ({
  id: event.id,
  slug: event.slug || event.id,
  title: event.title,
  short: event.short_description || event.short || "",
  image: event.cover_image || event.image,
  categoryLabel: event.category_label || event.category || "",
  location: event.location_name || event.location || event.city || "",
  date: event.start_at || event.date,
  end: event.end_at || null,
  time: event.start_at
    ? `${String(new Date(event.start_at).getHours()).padStart(2,"0")}:${String(new Date(event.start_at).getMinutes()).padStart(2,"0")}`
    : (event.time || ""),
  state: event.state, // "ongoing" | "upcoming" | "past" (dal backend, quando disponibile)
});

// Fallback client-side per calcolare lo stato SE il backend non lo fornisce.
// NB: se sono presenti le date, computiamo SEMPRE lato client per garantire
// un cambio di stato istantaneo (In corso → Concluso) allo scadere di end_at
// senza attendere refresh o un nuovo fetch di lista.
const computeState = (event) => {
  const now = new Date();
  const start = event.date ? new Date(event.date) : null;
  if (!start) return event.state || "upcoming";
  const end = event.end ? new Date(event.end) : start;
  if (start <= now && now <= end) return "ongoing";
  if (start > now) return "upcoming";
  return "past";
};

const OngoingBadge = () => (
  <span
    data-testid="event-badge-ongoing"
    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-medium text-white bg-[#12964A] px-2.5 py-1 rounded-full"
  >
    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
    In corso
  </span>
);

const EventCard = ({ event: raw, variant = "default" }) => {
  const event = norm(raw);
  if (!event.date) return null;
  const date = new Date(event.date);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const href = `/eventi/${event.slug}`;
  const state = computeState(event);
  const isOngoing = state === "ongoing";
  const isPast = state === "past";

  if (variant === "row") {
    return (
      <Link
        to={href}
        className={`group grid grid-cols-12 gap-6 py-8 border-b border-stone-200 hover:bg-stone-50/60 -mx-4 px-4 rounded-2xl transition-colors ${isPast ? "opacity-70" : ""}`}
        data-testid={`event-row-${event.slug}`}
        data-state={state}
      >
        <div className="col-span-3 md:col-span-2">
          <div className="font-display text-5xl md:text-6xl text-stone-950 leading-none">{day}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-2">{month} {year}</div>
        </div>
        <div className="col-span-9 md:col-span-7">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {isOngoing && <OngoingBadge />}
            {event.categoryLabel && (
              <span className="inline-block text-[11px] uppercase tracking-[0.18em] text-[#12964A]">
                {event.categoryLabel}
              </span>
            )}
          </div>
          <h3 className="font-display text-2xl md:text-3xl text-stone-950 leading-tight group-hover:text-[#12964A] transition-colors">
            {event.title}
          </h3>
          {event.short && <p className="text-stone-600 mt-2 max-w-2xl">{event.short}</p>}
          <div className="flex items-center gap-4 text-sm text-stone-500 mt-4">
            {event.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {event.location}
              </span>
            )}
            {event.time && <span>· {event.time}</span>}
          </div>
        </div>
        <div className="hidden md:flex col-span-3 items-center justify-end">
          <div className="w-12 h-12 rounded-full border border-stone-300 flex items-center justify-center group-hover:bg-stone-950 group-hover:border-stone-950 group-hover:text-white transition-colors">
            <ArrowUpRight className="w-4 h-4" />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className={`group relative overflow-hidden rounded-3xl bg-white card-shadow flex flex-col ${isPast ? "opacity-80" : ""}`}
      data-testid={`event-card-${event.slug}`}
      data-state={state}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {event.image && (
          <img src={event.image} alt={event.title} loading="lazy"
               className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/50 via-stone-950/5 to-transparent" />
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-2xl px-3 py-2 text-center">
          <div className="font-display text-2xl text-stone-950 leading-none">{day}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1">{month}</div>
        </div>
        <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
          {isOngoing && <OngoingBadge />}
          {event.categoryLabel && (
            <span className="text-[11px] uppercase tracking-[0.18em] text-white bg-stone-900/60 backdrop-blur px-3 py-1.5 rounded-full">
              {event.categoryLabel}
            </span>
          )}
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-display text-2xl text-stone-950 leading-tight group-hover:text-[#12964A] transition-colors">
          {event.title}
        </h3>
        {event.short && <p className="text-stone-600 mt-2 line-clamp-2">{event.short}</p>}
        <div className="flex flex-wrap items-center gap-3 text-sm text-stone-500 mt-4">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> {day} {month} {year}{event.time && ` · ${event.time}`}
          </span>
          {event.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {event.location}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
